// ===================================================================
// WORKFLOWS — Module Layout with nav tabs and inbox badge
// ===================================================================

import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { IconInbox, IconPlayerPlay, IconList, IconTemplate } from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import type { NavItem } from '../../types/nav';
import ModuleNav from '../../components/layout/ModuleNav';
import PageHeader from '../../components/ui/PageHeader';

const NAV_ITEMS: NavItem[] = [
  { label: 'Inbox',     path: '/workflows',           icon: IconInbox,       exact: true },
  { label: 'Active',    path: '/workflows/active',    icon: IconPlayerPlay },
  { label: 'All',       path: '/workflows/all',       icon: IconList },
  { label: 'Templates', path: '/workflows/templates', icon: IconTemplate,    minLevel: 'editor' },
];

const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '',      paths: ['/workflows', '/workflows/active', '/workflows/all'] },
  { label: 'Admin', paths: ['/workflows/templates'] },
];

export default function WorkflowLayout() {
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('workflows', NAV_ITEMS);

  const user = useAuthStore(s => s.user);
  const { steps, instances, loadAll } = useWorkflowStore();

  useEffect(() => { loadAll(); }, []);

  // Count pending inbox items for current user (role-aware)
  const { roles, userRoles } = useRoleStore();
  const inboxCount = useMemo(() => {
    if (!user?.email) return 0;
    const email = user.email.toLowerCase();
    const userAssignment = userRoles.find(ur => (ur.userEmail || '').toLowerCase() === email);
    const userRole = userAssignment ? roles.find(r => String(r.id) === userAssignment.roleId) : undefined;

    return steps.filter(st => {
      if (st.status !== 'pending' && st.status !== 'in_progress') return false;
      const inst = instances.find(i => String(i.Rid) === st.instanceId);
      if (!inst || inst.status !== 'active') return false;
      if ((st.assignee || '').toLowerCase() === email) return true;
      if (st.assigneeType === 'role' && userRole) {
        const stepRole = (st.assignee || '').toLowerCase();
        if (userRole.roleSlug && stepRole === userRole.roleSlug.toLowerCase()) return true;
        if (userRole.roleName && stepRole === userRole.roleName.toLowerCase()) return true;
      }
      return false;
    }).length;
  }, [steps, instances, user?.email, roles, userRoles]);

  // Inject inbox badge dynamically
  const itemsWithBadge = visibleItems.map(item =>
    item.path === '/workflows' && inboxCount > 0 ? { ...item, badge: inboxCount } : item
  );

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => itemsWithBadge.find(vi => vi.path === p))
      .filter(Boolean) as typeof itemsWithBadge,
  })).filter(g => g.items.length > 0);

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Workflows' }]}
        title="Workflows"
        subtitle="Custom workflow engine with templates, approvals, and task tracking"
      />
      <ModuleNav items={itemsWithBadge} groups={groups} />
      <Outlet />
    </div>
  );
}
